import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export type Session = {
  token: string;
  email: string;
  role: 'team' | 'speaker';
};

type AuthContextValue = {
  session: Session | null;
  setSession: (s: Session | null) => void;
  loading: boolean;
  /** True when this tab is using the preview-as-speaker sessionStorage path. */
  isPreview: boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEY = 'aotv.session';
const PREVIEW_KEY = 'aotv.preview-session';

/**
 * Tab-scoped preview session takes precedence over the normal localStorage
 * session — so an admin can open a "preview as speaker" link in a new tab
 * without overwriting their own admin session in the original tab.
 */
function hasPreviewSession(): boolean {
  return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PREVIEW_KEY) !== null;
}

function readSession(): { session: Session | null; isPreview: boolean } {
  if (hasPreviewSession()) {
    try {
      const raw = sessionStorage.getItem(PREVIEW_KEY);
      if (raw) return { session: JSON.parse(raw) as Session, isPreview: true };
    } catch {
      sessionStorage.removeItem(PREVIEW_KEY);
    }
  }
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw) {
    try {
      return { session: JSON.parse(raw) as Session, isPreview: false };
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
  return { session: null, isPreview: false };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sessionState, setSessionState] = useState<{
    session: Session | null;
    isPreview: boolean;
  }>({ session: null, isPreview: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSessionState(readSession());
    setLoading(false);
  }, []);

  const setSession = (s: Session | null) => {
    if (sessionState.isPreview) {
      // Stay in preview mode — write to sessionStorage so the admin's
      // localStorage doesn't get touched.
      if (s) sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(s));
      else sessionStorage.removeItem(PREVIEW_KEY);
    } else {
      if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
      else localStorage.removeItem(STORAGE_KEY);
    }
    setSessionState({ session: s, isPreview: sessionState.isPreview });
  };

  return (
    <AuthContext.Provider
      value={{
        session: sessionState.session,
        setSession,
        loading,
        isPreview: sessionState.isPreview,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function startPreviewSession(s: Session) {
  sessionStorage.setItem(PREVIEW_KEY, JSON.stringify(s));
}
